import React, { useState, useEffect, useCallback } from "react";
import { CreditCard, Plus, Search, LayoutGrid, List } from "lucide-react";
import { PageHeader, AppButton, ConfirmModal } from "../../ui";
import PlanCard from "./PlanCard";
import PlanFormModal from "./PlanFormModal";
import apiClient from "../../../utils/apiClient";
import "./plans.scss";

const EMPTY_FORM = {
  name: "",
  priceMonthly: 0,
  priceQuarterly: 0,
  priceYearly: 0,
  revenueShareType: "percentage",
  revenueSharePercent: 0,
  revenueShareFlatAmount: 0,
  revenueShareCadence: "once",
  allowBillingWithoutBatch: false,
  minActiveDaysForBilling: 5,
  extraStoragePricePer100GB: 0,
  maxStudentsPerBatch: 0,
  maxBatches: 0,
  maxCourses: 0,
  maxInstructors: 0,
  maxAdmins: 0,
  storageGB: 0,
  zoomV2: false,
  recordings: false,
  whatsapp: false,
  customDomain: false,
  aiMock: false,
  aiLearning: false,
  aiQuestionBank: false,
  aiDoubtSolving: false,
  feeManagement: true,
  enquiryManagement: true,
  zohoCrm: false,
  interviews: false,
  codingPractice: false,
  assignments: true,
  quizAssessment: false,
  customBranding: false,
  apiAccess: false,
  enquiryFormBuilder: false,
  secureVideoPlayer: false,
  multiBranch: false,
  maxBranches: 1,
  isPopular: false,
  isPublic: true,
};

const PlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid | list

  const fetchPlans = useCallback(async () => {
    try {
      const res = await apiClient.get("/sa/plans");
      setPlans(res.data.plans || []);
    } catch { /* keep empty */ }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleCreate = async (data) => {
    try {
      setFormLoading(true);
      await apiClient.post("/sa/plans", data);
      await fetchPlans();
      setShowForm(false);
      setFormData(EMPTY_FORM);
      setEditId(null);
    } catch { /* handle error */ }
    finally { setFormLoading(false); }
  };

  const handleEdit = (plan) => {
    const { _id, __v, createdAt, updatedAt, instituteCount, ...fields } = plan;
    setFormData({ ...EMPTY_FORM, ...fields });
    setEditId(plan._id);
    setShowForm(true);
  };

  const handleUpdate = async (data) => {
    try {
      setFormLoading(true);
      await apiClient.put(`/sa/plans/${editId}`, data);
      await fetchPlans();
      setShowForm(false);
      setFormData(EMPTY_FORM);
      setEditId(null);
    } catch { /* handle error */ }
    finally { setFormLoading(false); }
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/sa/plans/${deleteId}`);
      setPlans((prev) => prev.filter((p) => p._id !== deleteId));
      setDeleteId(null);
    } catch { /* handle error */ }
  };

  const handleToggleVisibility = async (planId, makePublic) => {
    try {
      await apiClient.put(`/sa/plans/${planId}`, { isPublic: makePublic });
      setPlans((prev) =>
        prev.map((p) => (p._id === planId ? { ...p, isPublic: makePublic } : p))
      );
    } catch { /* handle error */ }
  };

  const handleSetDefaultTrial = async (planId) => {
    try {
      const { data } = await apiClient.patch(`/sa/plans/${planId}/set-default-trial`);
      const nowDefault = data.plan.isDefaultTrial;
      setPlans((prev) =>
        prev.map((p) => ({
          ...p,
          isDefaultTrial: p._id === planId ? nowDefault : false,
        }))
      );
    } catch { /* handle error */ }
  };

  const handleSetRecommended = async (planId) => {
    try {
      const { data } = await apiClient.patch(`/sa/plans/${planId}/recommend`);
      const nowPopular = data.plan.isPopular;
      setPlans((prev) =>
        prev.map((p) => ({
          ...p,
          isPopular: p._id === planId ? nowPopular : false,
        }))
      );
    } catch { /* handle error */ }
  };

  const handleSetFreePlan = async (planId) => {
    try {
      const { data } = await apiClient.patch(`/sa/plans/${planId}/set-free-plan`);
      const nowFree = data.plan.isFree;
      setPlans((prev) =>
        prev.map((p) => ({
          ...p,
          isFree: p._id === planId ? nowFree : false,
        }))
      );
    } catch { /* handle error */ }
  };

  /* ── Filter plans by search ── */
  const filtered = plans.filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  /* ── Quick stats ── */
  const totalInstitutes = plans.reduce((s, p) => s + (p.instituteCount || 0), 0);
  const publicCount = plans.filter((p) => p.isPublic !== false).length;
  const trialPlan = plans.find((p) => p.isDefaultTrial);
  const freePlan = plans.find((p) => p.isFree);

  return (
    <div className="sa-plans">
      <PageHeader
        title="Plans"
        description="Manage subscription plans and feature limits"
        icon={CreditCard}
        actions={
          <AppButton
            variant="default"
            size="sm"
            onClick={() => { setFormData(EMPTY_FORM); setEditId(null); setShowForm(true); }}
          >
            <Plus size={16} /> Create Plan
          </AppButton>
        }
      />

      {/* ── Quick Stats Bar ── */}
      <div className="sa-plans__stats">
        <div className="sa-plans__stat">
          <span className="sa-plans__stat-value">{plans.length}</span>
          <span className="sa-plans__stat-label">Total Plans</span>
        </div>
        <div className="sa-plans__stat">
          <span className="sa-plans__stat-value">{publicCount}</span>
          <span className="sa-plans__stat-label">Public</span>
        </div>
        <div className="sa-plans__stat">
          <span className="sa-plans__stat-value">{plans.length - publicCount}</span>
          <span className="sa-plans__stat-label">Private</span>
        </div>
        <div className="sa-plans__stat">
          <span className="sa-plans__stat-value">{totalInstitutes}</span>
          <span className="sa-plans__stat-label">Institutes</span>
        </div>
        <div className="sa-plans__stat">
          <span className="sa-plans__stat-value sa-plans__stat-value--trial">
            {trialPlan ? trialPlan.name : "None"}
          </span>
          <span className="sa-plans__stat-label">Default Trial</span>
        </div>
        <div className="sa-plans__stat">
          <span className="sa-plans__stat-value sa-plans__stat-value--trial">
            {freePlan ? freePlan.name : "None"}
          </span>
          <span className="sa-plans__stat-label">Free Plan</span>
        </div>
      </div>

      {/* ── Toolbar: Search + View Toggle ── */}
      <div className="sa-plans__toolbar">
        <div className="sa-plans__search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search plans…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="sa-plans__view-toggle">
          <button
            className={`sa-plans__view-btn ${viewMode === "grid" ? "sa-plans__view-btn--active" : ""}`}
            onClick={() => setViewMode("grid")}
            title="Grid view"
          >
            <LayoutGrid size={16} />
          </button>
          <button
            className={`sa-plans__view-btn ${viewMode === "list" ? "sa-plans__view-btn--active" : ""}`}
            onClick={() => setViewMode("list")}
            title="List view"
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* ── Plans Display ── */}
      {filtered.length === 0 ? (
        <div className="sa-plans__empty">
          <CreditCard size={40} strokeWidth={1.5} />
          <h3>No plans found</h3>
          <p>{search ? "Try adjusting your search." : "Create your first subscription plan."}</p>
          {!search && (
            <AppButton variant="default" size="sm" onClick={() => { setFormData(EMPTY_FORM); setEditId(null); setShowForm(true); }}>
              <Plus size={16} /> Create Plan
            </AppButton>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="sa-plans__grid">
          {filtered.map((plan) => (
            <PlanCard
              key={plan._id}
              plan={plan}
              onEdit={handleEdit}
              onDelete={setDeleteId}
              onToggleVisibility={handleToggleVisibility}
              onSetRecommended={handleSetRecommended}
              onSetDefaultTrial={handleSetDefaultTrial}
              onSetFreePlan={handleSetFreePlan}
              isPopular={!!plan.isPopular}
            />
          ))}
        </div>
      ) : (
        <div className="sa-plans__list">
          {filtered.map((plan) => (
            <PlanCard
              key={plan._id}
              plan={plan}
              onEdit={handleEdit}
              onDelete={setDeleteId}
              onToggleVisibility={handleToggleVisibility}
              onSetRecommended={handleSetRecommended}
              onSetDefaultTrial={handleSetDefaultTrial}
              onSetFreePlan={handleSetFreePlan}
              isPopular={!!plan.isPopular}
              listMode
            />
          ))}
        </div>
      )}

      <PlanFormModal
        show={showForm}
        onHide={() => { setShowForm(false); setEditId(null); setFormData(EMPTY_FORM); }}
        onSubmit={editId ? handleUpdate : handleCreate}
        formData={formData}
        setFormData={setFormData}
        loading={formLoading}
        isEdit={!!editId}
      />

      <ConfirmModal
        show={!!deleteId}
        onHide={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Plan"
        message={
          (() => {
            const target = plans.find((p) => p._id === deleteId);
            if (target?.isFree) return `Cannot delete "${target.name}" — it is the free/downgrade plan. Remove the free plan designation first.`;
            if (target?.isDefaultTrial) return `Cannot delete "${target.name}" — it is the default trial plan. Remove the trial designation first.`;
            if ((target?.instituteCount || 0) > 0) return `Cannot delete "${target.name}" — ${target.instituteCount} institute(s) are on this plan.`;
            return "Are you sure? Institutes on this plan will need to be reassigned.";
          })()
        }
        confirmText="Delete"
        confirmDisabled={(() => {
          const target = plans.find((p) => p._id === deleteId);
          return !!(target?.isFree || target?.isDefaultTrial || (target?.instituteCount || 0) > 0);
        })()}
      />
    </div>
  );
};

export default PlansPage;
